import { memo } from "react";
import { NavLink } from "react-router-dom";
import { prefetchRoute, type NavItem } from "@/components/feature/navigation";

const LOGO_SRC =
  "https://storage.helloreaddy.io/project_files/b8583382-38f6-4b44-9388-11a95eaee93e/fa07c2a6-a0eb-4342-b4d2-b90375c04059_compressed_LOGO-NEWLEXBABY-1.webp";

interface SidebarNavProps {
  items: NavItem[];
  bottomItems: NavItem[];
  userName: string;
  roleLabel: string;
  initials: string;
  onSignOut: () => void;
  onItemClick?: () => void;
}

/**
 * Khung menu bên trái.
 *
 * Được bọc memo: chỉ vẽ lại khi thật sự đổi người dùng / quyền / danh sách mục.
 * Mọi thay đổi dữ liệu ở trang bên phải (bảng, biểu đồ, thông báo) đều KHÔNG
 * kéo theo việc render lại menu.
 *
 * Trạng thái "đang chọn" do chính từng NavLink tự lo (nó theo dõi URL), nên khi
 * bấm chuyển trang chỉ đúng 1-2 mục đổi màu sáng — menu không bị khựng.
 */
function SidebarNavBase({
  items,
  bottomItems,
  userName,
  roleLabel,
  initials,
  onSignOut,
  onItemClick,
}: SidebarNavProps) {
  const renderItem = (item: NavItem) => (
    <NavLink
      key={item.to}
      to={item.to}
      end={item.hasChild}
      onClick={onItemClick}
      onMouseEnter={() => prefetchRoute(item.to)}
      onFocus={() => prefetchRoute(item.to)}
      onTouchStart={() => prefetchRoute(item.to)}
      className={({ isActive }) =>
        `flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
          isActive
            ? "bg-primary-100 text-primary-700"
            : "text-foreground-600 hover:bg-background-100 hover:text-foreground-950"
        }`
      }
    >
      <span className="flex h-6 w-6 items-center justify-center">
        <i className={`${item.icon} text-lg`} />
      </span>
      <span className="whitespace-nowrap">{item.label}</span>
    </NavLink>
  );

  return (
    <div className="flex h-full flex-col">
      <div className="flex shrink-0 items-center gap-3 px-4 py-5">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center">
          <img src={LOGO_SRC} alt="NewLexBaby" className="h-10 w-10 object-contain" />
        </div>
        <div className="min-w-0 leading-tight">
          <p className="truncate text-sm font-bold text-foreground-950">NewLexBaby</p>
          <p className="truncate text-xs text-foreground-500">Quản lý kho &amp; kiểm hàng</p>
        </div>
      </div>

      <nav className="mt-2 flex min-h-0 flex-1 flex-col overflow-y-auto px-3 pb-3">
        <div className="space-y-1">{items.map(renderItem)}</div>

        {bottomItems.length > 0 && (
          <div className="mt-2 space-y-1 border-t border-background-200 pt-3">
            {bottomItems.map(renderItem)}
          </div>
        )}
      </nav>

      <div className="border-t border-background-200 p-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-500 text-sm font-bold text-white">
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-foreground-950">{userName}</p>
            <p className="truncate text-xs text-foreground-500">{roleLabel}</p>
          </div>
          <button
            type="button"
            onClick={onSignOut}
            title="Đăng xuất"
            aria-label="Đăng xuất"
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg text-foreground-500 transition-colors hover:bg-background-100 hover:text-foreground-950"
          >
            <i className="ri-logout-box-r-line text-lg" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default memo(SidebarNavBase);